import { X } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useAnimation } from "@/features/animation";
import { useProject } from "@/features/project";

const AGENTS_URL = "https://ai.gliadirector.com/agents";

const DEMO_VIDEOS = [
  "https://static-gstudio.gliacloud.com/10903/files/f4083b4b982534a70d14ba0a3bf8de7790c3a474.mp4",
  "https://static-gstudio.gliacloud.com/10903/files/021120c5de916c3becf3068f000ce22627684906.mp4",
  "https://static-gstudio.gliacloud.com/10903/files/9ac212f31135ab34d23e2852b25e34476164a67c.mp4",
];

const AGENT_DISCOVERY_KEY = "hasSeenAgentDiscovery";

export const AgentDiscoveryModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  const { controller } = useAnimation();
  const { messages } = useProject();

  /**
   * Toggles the Agent Discovery modal.
   * If showing, it will only show if the user hasn't seen it before.
   * If hiding, it marks that the user has seen it.
   * Returns true if the modal is shown after the call, false otherwise.
   */
  const toggleAgentDiscovery = useCallback((show: boolean) => {
    if (show) {
      if (localStorage.getItem(AGENT_DISCOVERY_KEY) === "true") return false;
      setIsOpen(true);
      return true;
    } else {
      localStorage.setItem(AGENT_DISCOVERY_KEY, "true");
      setIsOpen(false);
      return false;
    }
  }, []);

  const hasMessage = messages.length > 0;
  useEffect(() => {
    if (hasMessage) {
      controller.onPlaybackComplete = () => {
        const shown = toggleAgentDiscovery(true);
        if (shown) controller.pause();
      };
      controller.onPause = () => {
        toggleAgentDiscovery(true);
      };
    } else {
      controller.onPlaybackComplete = null;
      controller.onPause = null;
    }
  }, [hasMessage, controller, toggleAgentDiscovery]);

  if (!isOpen) return null;

  const dismiss = () => toggleAgentDiscovery(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-slate-900 rounded-lg shadow-xl border border-slate-700 w-full max-w-2xl mx-4">
        <div className="flex justify-between p-3">
          <h2 className="text-lg font-semibold text-slate-200 mb-2">
            Is this the type of video you're looking for?
          </h2>
          <button
            onClick={dismiss}
            className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-slate-200"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 pb-6">
          <p className="text-sm text-slate-400 mb-4">
            We have specialized Agents for specific styles.
          </p>

          <div className="grid grid-cols-3 gap-3 mb-6">
            {DEMO_VIDEOS.map((url) => (
              <video
                key={url}
                src={url}
                autoPlay
                muted
                loop
                playsInline
                className="w-full aspect-video object-cover rounded-lg border border-slate-700"
              />
            ))}
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={dismiss}
              className="px-4 py-2 text-sm rounded bg-slate-700 text-slate-200 hover:bg-slate-600 transition-colors"
            >
              Continue
            </button>
            <a
              href={AGENTS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-500 transition-colors"
            >
              Explore Agents →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
